/**
 * OS MINI-DESENHOS DE ENQUADRAMENTO — `T8.16` e `T8.34`.
 *
 * O achado: *"O modal de geração usa termos de cinema como 'Contra-plongée', 'Plongée' e 'Ângulo
 * holandês'."* A sugestão traz a tradução pronta e pede mais uma coisa: **"com um mini-desenho de
 * cada"**.
 *
 * ── POR QUE O DESENHO NÃO É ENFEITE ──────────────────────────────────────────────────────────
 *
 * "De baixo para cima" é melhor que "Contra-plongée" e ainda assim é ambíguo: baixo do quê? Uma
 * criança de onze anos lê a frase e não sabe se a câmera está no chão ou se o personagem está no
 * alto. O desenho responde em um olhar, e é o mesmo motivo pelo qual a tela de formatos de página
 * mostra a proporção da folha em vez de dizer os números.
 *
 * ── A GRAMÁTICA DOS DESENHOS, e ela é uma só ─────────────────────────────────────────────────
 *
 * Em todos: o **retângulo é o personagem** e o **ponto cheio é de onde se olha**. Quem entende o
 * primeiro entende os oito, porque a regra não muda de um para o outro. Uma linguagem de ícone que
 * troca de convenção no meio da lista ensina duas coisas erradas em vez de uma certa.
 *
 * `currentColor` em tudo: o chip já define a cor do texto, e o desenho tem que acompanhar o tema
 * (a lição do T1.29, em que uma tela com `color: white` cravado virou branco sobre fundo claro).
 * `aria-hidden` porque o rótulo ao lado já diz o nome — leitor de tela não precisa ouvir duas vezes.
 */

const CAIXA = { width: 26, height: 26, viewBox: "0 0 26 26" } as const;

/** O personagem: retângulo em pé, fino, sempre no mesmo lugar. */
function Figura({ x = 9, y = 6, w = 8, h = 14 }) {
  return (
    <rect
      x={x}
      y={y}
      width={w}
      height={h}
      rx="1.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
    />
  );
}

/** De onde se olha. Cheio de propósito: é o único elemento cheio do desenho. */
function Olho({ cx, cy }: { cx: number; cy: number }) {
  return <circle cx={cx} cy={cy} r="2.4" fill="currentColor" />;
}

function Raio({ x1, y1, x2, y2 }: { x1: number; y1: number; x2: number; y2: number }) {
  return (
    <line
      x1={x1}
      y1={y1}
      x2={x2}
      y2={y2}
      stroke="currentColor"
      strokeWidth="1.2"
      strokeDasharray="2 2"
    />
  );
}

function Svg({ children }: { children: React.ReactNode }) {
  return (
    <svg {...CAIXA} aria-hidden="true" focusable="false" className="desenho-de-enquadramento">
      {children}
    </svg>
  );
}

/** Olho na mesma altura do meio da figura. */
export const NivelDoOlhar = () => (
  <Svg>
    <Figura />
    <Olho cx={3} cy={13} />
    <Raio x1={5.5} y1={13} x2={9} y2={13} />
  </Svg>
);

/** Olho embaixo, olhando para cima: "de baixo para cima". */
export const DeBaixoParaCima = () => (
  <Svg>
    <Figura />
    <Olho cx={3.5} cy={22} />
    <Raio x1={5.5} y1={21} x2={10} y2={8} />
  </Svg>
);

/** Olho em cima, olhando para baixo. */
export const DeCimaParaBaixo = () => (
  <Svg>
    <Figura />
    <Olho cx={3.5} cy={4} />
    <Raio x1={5.5} y1={5} x2={10} y2={18} />
  </Svg>
);

/** Olho direto em cima: a figura aparece de topo, então ela vira um quadrado largo. */
export const DeCimaDeTudo = () => (
  <Svg>
    <Figura x={7} y={12} w={12} h={9} />
    <Olho cx={13} cy={3.5} />
    <Raio x1={13} y1={6} x2={13} y2={11} />
  </Svg>
);

/** A figura inclinada: o que muda é o mundo, não de onde se olha. */
export const Inclinado = () => (
  <Svg>
    <g transform="rotate(18 13 13)">
      <Figura />
    </g>
    <Olho cx={3} cy={13} />
  </Svg>
);

/** Bem de perto: a moldura corta, e só a cabeça cabe. */
export const BemDePerto = () => (
  <Svg>
    <rect
      x="2"
      y="2"
      width="22"
      height="22"
      rx="2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.1"
      strokeDasharray="3 2"
    />
    <circle cx="13" cy="13" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
  </Svg>
);

/** Da cintura para cima: a moldura corta a figura no meio. */
export const DaCinturaParaCima = () => (
  <Svg>
    <rect
      x="2"
      y="2"
      width="22"
      height="22"
      rx="2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.1"
      strokeDasharray="3 2"
    />
    <circle cx="13" cy="8" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M8 24 L8 14 Q13 11 18 14 L18 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
    />
  </Svg>
);

/** Corpo inteiro: a figura cabe dentro da moldura com folga. */
export const CorpoInteiro = () => (
  <Svg>
    <rect
      x="2"
      y="2"
      width="22"
      height="22"
      rx="2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.1"
      strokeDasharray="3 2"
    />
    <circle cx="13" cy="7.5" r="2.2" fill="none" stroke="currentColor" strokeWidth="1.4" />
    <Figura x={10} y={10} w={6} h={11} />
  </Svg>
);
